import { newUuid } from '@overlayqa/shared';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useExtensionDetection } from './useExtensionDetection';
import { useAppSession } from './useAppSession';
import { capture } from '@/lib/analytics';
import { log } from '@/lib/logger';
import {
  readPendingExtensionOpen,
  clearPendingExtensionOpen,
  pendingOpenWasConfirmed,
  pendingOpenBelongsToSession,
  type PendingExtensionOpen,
} from '@/lib/pendingExtensionOpen';
import { EXTENSION_AUTH_FAILED_EVENT, EXTENSION_AUTH_REQUEST_EVENT } from '@/lib/extensionAuthAcknowledgment';

type PendingInfo = PendingExtensionOpen;

interface UsePendingExtensionOpenResult {
  hasPendingOpen: boolean;
  pendingUrl: string | null;
  extensionDetected: boolean;
  error: string | null;
  openPendingSite: () => void;
}

export function usePendingExtensionOpen(): UsePendingExtensionOpenResult {
  const { data: session } = useAppSession();
  const [pending, setPending] = useState<PendingInfo | null>(() => readPendingExtensionOpen());
  const hasPending = pendingOpenBelongsToSession(pending, session);
  const reliableHandoff = pending?.source === 'team_review' || pending?.teamId !== session?.activeTeamId;
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const firedRef = useRef<string | null>(null);
  const pendingKey = pending ? `${pending.userId}:${pending.teamId}:${pending.projectId}:${pending.handoffId}:${pending.savedAt}` : null;

  const { detected } = useExtensionDetection({
    poll: hasPending,
    intervalMs: 3000,
  });

  const fireOpenSite = useCallback((info: PendingInfo) => {
    log.info('[usePendingExtensionOpen] extension detected, firing open-site', { url: info.url });
    // Forward the FULL pending info including projectId/projectName/projectSlug/teamId.
    // Without these, the background's ONBOARDING_OPEN_SITE handler skips
    // pre-seeding the selected project and onboarding milestones, so the sidebar
    // opens to the project list instead of the onboarded project.
    window.postMessage(
      {
        type: 'OVERLAYQA_OPEN_SITE',
        payload: info,
      },
      window.location.origin,
    );
    capture('pending_extension_open_resumed', {
      url_type: info.urlType,
      url_value: info.url,
    });
    // Keep the handoff until the extension reports a rendered sidebar. A tab
    // being created or an install being detected does not establish success.
  }, []);

  // Wait for BOTH extension detection AND auth bridge completion
  // before firing the open-site message. Without this, the open-site
  // fires before the auth bridge writes the session token to
  // chrome.storage, causing the sidebar to open in anonymous mode.
  const [authBridged, setAuthBridged] = useState(() => {
    // Check if the bridge already fired before we mounted (the bridge
    // writes a dedupe entry to sessionStorage on every successful push)
    try {
      const raw = sessionStorage.getItem('oqa_last_extension_auth_at');
      if (!raw) return false;
      // A legacy sentinel is usable only for this account's default workspace.
      try { const entry = JSON.parse(raw); return entry.teamId === pending?.teamId && (!entry.userId || entry.userId === session?.user.id); }
      catch { return pending?.teamId === session?.activeTeamId; }
    } catch { return false; }
  });

  useEffect(() => {
    if (!hasPending || authBridged) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail ? detail.userId === pending?.userId && detail.teamId === pending?.teamId : pending?.teamId === session?.activeTeamId) {
        setError(null);
        setAuthBridged(true);
      }
    };
    window.addEventListener('overlayqa-auth-bridged', handler);
    return () => window.removeEventListener('overlayqa-auth-bridged', handler);
  }, [hasPending, authBridged, pending, session]);

  useEffect(() => {
    if (!hasPending || !detected || authBridged || error) return;
    // Let the App-level bridge install its listener before requesting the
    // target workspace. The bridge resolves that intent against this session.
    const timer = setTimeout(() => window.dispatchEvent(new Event(EXTENSION_AUTH_REQUEST_EVENT)), 0);
    return () => clearTimeout(timer);
  }, [hasPending, detected, authBridged, pendingKey, retry, error]);

  useEffect(() => {
    const failed = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.userId === pending?.userId && detail?.handoffId === pending?.handoffId) {
        setError(detail.message || 'The extension could not connect. Try again.');
        setAuthBridged(false);
      }
    };
    window.addEventListener(EXTENSION_AUTH_FAILED_EVENT, failed);
    return () => window.removeEventListener(EXTENSION_AUTH_FAILED_EVENT, failed);
  }, [pending]);

  useEffect(() => {
    if (!hasPending || !detected || !authBridged || !session || error) return;
    // Old unscoped handoffs cannot safely identify the account they belong to.
    if (!pending || !pendingOpenBelongsToSession(pending, session)) return;
    const requestId = newUuid();
    let answered = false;
    const receive = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      if (event.data?.type === 'OVERLAYQA_OPEN_SITE_RESULT' && event.data.handoffId === pending.handoffId && event.data.result?.success === false) {
        setError('The site could not open in the extension. Try again.');
        return;
      }
      if (event.data?.type !== 'OVERLAYQA_CONFIRMED_SIDEBAR_OPENS' || event.data.requestId !== requestId) return;
      const current = readPendingExtensionOpen();
      if (!current || current.savedAt !== pending.savedAt || current.handoffId !== pending.handoffId || current.userId !== session.user.id) {
        setPending(current);
        return;
      }
      const result = event.data.result;
      if (!result || !Array.isArray(result.receipts)) return;
      answered = true;
      if (result.userId === session.user.id && result.receipts.some((receipt: unknown) => pendingOpenWasConfirmed(current, receipt))) {
        clearPendingExtensionOpen();
        setPending(null);
        return;
      }
      const matchingTeam = result.teamId === current.teamId
        || (!reliableHandoff && result.teamId === undefined && current.teamId === session.activeTeamId);
      if (result.userId !== session.user.id || !matchingTeam) {
        firedRef.current = null;
        setAuthBridged(false);
        return;
      }
      if (firedRef.current !== pendingKey) {
        firedRef.current = pendingKey;
        fireOpenSite(current);
      }
    };
    const query = () => window.postMessage({ type: 'OVERLAYQA_QUERY_SIDEBAR_OPENS', requestId }, window.location.origin);
    window.addEventListener('message', receive);
    query();
    const interval = window.setInterval(query, 3000);
    const deadline = window.setTimeout(() => { if (!answered && reliableHandoff) setError('The extension did not respond. Update it in Chrome and try again.'); }, 10_000);
    return () => { clearInterval(interval); clearTimeout(deadline); window.removeEventListener('message', receive); };
  }, [hasPending, detected, authBridged, pending, pendingKey, fireOpenSite, session, error, retry, reliableHandoff]);

  return {
    hasPendingOpen: hasPending,
    pendingUrl: hasPending ? pending?.url ?? null : null,
    extensionDetected: detected,
    error,
    openPendingSite: () => {
      if (!hasPending || !pending) return;
      // Existing onboarding/scheduled-install flows offered this explicit
      // action before extensions could acknowledge auth or report receipts.
      // Preserve it in the current workspace; Team Review and workspace
      // changes keep the stronger handshake and rendered-sidebar proof.
      if (!reliableHandoff) {
        setError(null);
        firedRef.current = pendingKey;
        fireOpenSite(pending);
        return;
      }
      firedRef.current = null;
      setError(null);
      setAuthBridged(false);
      setRetry(value => value + 1);
    },
  };
}
